/**
 * NER-4488 — Helpers puros del wizard de plantillas de combos.
 *
 * Todo lo que no toca React ni la API vive acá para poder testearlo con Jest:
 * preselección de categorías por pistas, estado inicial de los espacios,
 * cálculo de la suma de componentes y del precio sugerido, y el mapeo
 * espacios → payload del item combo + grupos de modificadores.
 *
 * Contrato de datos (fase 1, sin cambios de backend):
 *   - espacio `fixed`  → `comboItems.fixed[]` `{ itemId, name, quantity }`
 *   - espacio `choice` → grupo de modificadores `{ name, minSelect, maxSelect,
 *                        required, options[{ name, price }] }` adjunto al combo
 *                        via `modifierGroups[]`
 *   - metadatos para reabrir el wizard → `comboItems.templateId`,
 *     `comboItems.templateVersion`, `comboItems.slots[]`
 */

import {
  COMBO_TEMPLATE_VERSION,
  CUSTOM_TEMPLATE_ID,
  NEW_COMBOS_CATEGORY,
  getComboTemplate,
} from '../data/comboTemplates';
import { normalizeText } from './text';

/** Re-export para compatibilidad: la implementación vive en utils/text.js. */
export { normalizeText };

/** Categorías que tienen al menos un producto (no combo) para elegir. */
export function selectableCategories(categories) {
  return (categories || []).filter((cat) =>
    (cat.items || []).some((item) => !item.isCombo && item.status !== 'deleted')
  );
}

/**
 * Devuelve el `categoryId` de la primera categoría con productos cuyo nombre
 * contenga alguna de las pistas. Cadena vacía si ninguna matchea.
 */
export function guessCategoryId(categories, hints) {
  const candidates = selectableCategories(categories);
  for (const hint of hints || []) {
    const needle = normalizeText(hint).trim();
    if (!needle) continue;
    const found = candidates.find((cat) => normalizeText(cat.name).includes(needle));
    if (found) return found.categoryId;
  }
  return '';
}

/** Mapa itemId → item (con `categoryId` y `categoryName` anexados). */
export function indexItems(categories) {
  const byId = new Map();
  for (const cat of categories || []) {
    for (const item of cat.items || []) {
      if (item.status === 'deleted') continue;
      byId.set(item.itemId, { ...item, categoryId: cat.categoryId, categoryName: cat.name });
    }
  }
  return byId;
}

/** Estado inicial de un espacio del wizard a partir de la definición de la plantilla. */
export function slotStateFromDefinition(definition, categories) {
  return {
    slotId: definition.id,
    kind: definition.kind,
    labelKey: definition.labelKey,
    quantity: definition.kind === 'fixed' ? definition.quantity || 1 : undefined,
    min: definition.kind === 'choice' ? (definition.min ?? 1) : undefined,
    max: definition.kind === 'choice' ? (definition.max ?? 1) : undefined,
    categoryId: guessCategoryId(categories, definition.hints),
    itemIds: [],
    extraPrices: {},
    groupId: null,
  };
}

/** Estado inicial de todos los espacios de una plantilla. */
export function buildInitialSlots(templateId, categories) {
  const template = getComboTemplate(templateId);
  if (!template) return [];
  return template.slots.map((definition) => slotStateFromDefinition(definition, categories));
}

/** Precio efectivo de una opción dentro de un espacio a elección. */
export function optionPrice(slot, item) {
  const extra = Number(slot.extraPrices?.[item.itemId] || 0);
  return Number(item.price || 0) + extra;
}

/**
 * Suma de componentes: fijos a precio × cantidad; a elección, la opción más
 * barata × mínimo (si el mínimo es 0 el espacio no suma). Ignora items que ya
 * no existen en la carta.
 */
export function computeComponentsSum(slots, byId) {
  let sum = 0;
  const breakdown = [];
  for (const slot of slots || []) {
    const items = (slot.itemIds || []).map((id) => byId.get(id)).filter(Boolean);
    let amount = 0;
    if (slot.kind === 'fixed') {
      amount = items.reduce((acc, item) => acc + Number(item.price || 0), 0) * (slot.quantity || 1);
    } else if (items.length > 0 && (slot.min || 0) > 0) {
      const cheapest = Math.min(...items.map((item) => optionPrice(slot, item)));
      amount = cheapest * slot.min;
    }
    sum += amount;
    breakdown.push({ slotId: slot.slotId, amount });
  }
  return { sum, breakdown };
}

/** Precio sugerido: descuento sobre la suma, redondeado a la decena. */
export function suggestPrice(sum, discountPct) {
  const base = Number(sum) || 0;
  if (base <= 0) return 0;
  const pct = Math.min(Math.max(Number(discountPct) || 0, 0), 90);
  const raw = base * (1 - pct / 100);
  return Math.max(0, Math.round(raw / 10) * 10);
}

/** Descuento implícito (entero, 0..100) entre suma y precio final. */
export function impliedDiscountPct(sum, price) {
  const base = Number(sum) || 0;
  const final = Number(price) || 0;
  if (base <= 0 || final >= base) return 0;
  return Math.round((1 - final / base) * 100);
}

/**
 * Valida los espacios. Devuelve `{ slotId, reason }` por cada problema:
 *   - `empty`: sin items
 *   - `tooFew`: a elección con menos opciones que el mínimo
 *   - `range`: mínimo mayor que máximo o valores inválidos
 */
export function validateSlots(slots) {
  const errors = [];
  for (const slot of slots || []) {
    const count = (slot.itemIds || []).length;
    if (count === 0) {
      errors.push({ slotId: slot.slotId, reason: 'empty' });
      continue;
    }
    if (slot.kind === 'choice') {
      const min = Number(slot.min);
      const max = Number(slot.max);
      if (Number.isNaN(min) || Number.isNaN(max) || min < 0 || max < 1 || min > max) {
        errors.push({ slotId: slot.slotId, reason: 'range' });
      } else if (count < Math.max(min, 1)) {
        errors.push({ slotId: slot.slotId, reason: 'tooFew' });
      }
    } else if (!(Number(slot.quantity) >= 1)) {
      errors.push({ slotId: slot.slotId, reason: 'range' });
    }
  }
  return errors;
}

/** Nombre del grupo de modificadores que materializa un espacio a elección. */
export function slotGroupName(comboName, slotLabel) {
  return `${(comboName || '').trim()} · ${slotLabel}`.trim();
}

/**
 * Body para crear/actualizar el grupo de modificadores de un espacio a elección.
 * Si se pasa el grupo existente, conserva los `id` de las opciones cuyo nombre
 * coincide (el backend los respeta en el PUT) para no romper pedidos en curso.
 */
export function buildModifierGroupBody(slot, slotLabel, comboName, byId, existingGroup) {
  const existingByName = new Map(
    (existingGroup?.options || []).map((opt) => [normalizeText(opt.name), opt.id])
  );
  const options = (slot.itemIds || [])
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((item) => {
      const option = {
        name: item.name,
        price: Number(slot.extraPrices?.[item.itemId] || 0),
        available: true,
      };
      const previousId = existingByName.get(normalizeText(item.name));
      if (previousId) option.id = previousId;
      return option;
    });
  const min = Math.max(0, Number(slot.min) || 0);
  const max = Math.max(1, Number(slot.max) || 1, min);
  return {
    name: slotGroupName(comboName, slotLabel),
    minSelect: min,
    maxSelect: max,
    required: min > 0,
    options,
  };
}

/**
 * Mapa `optionId → itemId` de un espacio a elección (RES-32).
 *
 * El grupo de modificadores del espacio se crea con una opción por producto, con el nombre del
 * producto; los `optionId` los asigna el backend. Guardar el mapa en la metadata del espacio es
 * lo que le permite al motor de consumo saber qué producto eligió el cliente — si no, tendría
 * que adivinarlo por el nombre.
 *
 * El mapa se arma por POSICIÓN: `buildModifierGroupBody` crea una opción por producto en el
 * orden de `slot.itemIds` y el backend las devuelve en ese mismo orden. Emparejar por nombre
 * —como se hacía hasta RES-61— rompía con dos productos que normalizan igual ("Agua" 500 ml y
 * "Agua" 1 L, o el mismo producto duplicado en dos categorías): el `Map` se quedaba con el
 * último y las dos opciones terminaban apuntando al mismo producto, así que el cliente elegía
 * una y el motor descontaba el insumo de la otra, sin que nada avisara. El nombre queda solo
 * como respaldo para una opción que no tenga posición (un grupo con más opciones que productos).
 *
 * @param {Object} slot - Espacio
 * @param {Object} savedGroup - Grupo tal como lo devolvió el backend (con los ids de opción)
 * @param {Map} byId - Mapa itemId → producto
 * @returns {Object} Mapa optionId → itemId
 */
export function optionItemIdsFor(slot, savedGroup, byId) {
  const items = (slot?.itemIds || []).map((id) => byId.get(id)).filter(Boolean);
  const byName = new Map(items.map((item) => [normalizeText(item.name), item.itemId]));
  const map = {};
  (savedGroup?.options || []).forEach((option, index) => {
    if (!option?.id) return;
    const itemId = items[index]?.itemId || byName.get(normalizeText(option.name));
    if (itemId) map[option.id] = itemId;
  });
  return map;
}

/** Serialización compacta de un espacio para `comboItems.slots[]` (round-trip). */
export function serializeSlot(slot, groupId, optionItemIds) {
  const base = {
    slotId: slot.slotId,
    kind: slot.kind,
    labelKey: slot.labelKey,
    itemIds: [...(slot.itemIds || [])],
  };
  if (slot.label) base.label = slot.label;
  if (slot.kind === 'fixed') {
    base.quantity = Number(slot.quantity) || 1;
  } else {
    base.min = Number(slot.min) || 0;
    base.max = Number(slot.max) || 1;
    base.extraPrices = { ...(slot.extraPrices || {}) };
    base.groupId = groupId || slot.groupId || null;
    // RES-32: qué producto representa cada opción, para el motor de consumo.
    // Gana el mapa recién calculado, pero si viene vacío se conserva el que ya tenía el espacio
    // en vez de borrarlo: `{}` es truthy, así que el fallback nunca llegaba a usarse y reguardar
    // un combo sin poder recalcular el mapa lo dejaba sin él, en silencio (RES-61).
    const mapped =
      optionItemIds && Object.keys(optionItemIds).length > 0 ? optionItemIds : slot.optionItemIds;
    if (mapped && Object.keys(mapped).length > 0) base.optionItemIds = { ...mapped };
  }
  return base;
}

/**
 * Payload del item combo (POST /items o PUT /items/{id}).
 * `groupIdsBySlot` mapea slotId → groupId ya creado/actualizado, y `optionItemIdsBySlot`
 * slotId → { optionId: itemId } (RES-32).
 */
export function buildComboItemPayload({
  templateId,
  slots,
  groupIdsBySlot,
  optionItemIdsBySlot,
  pricing,
  presentation,
  byId,
}) {
  const fixed = [];
  const modifierGroups = [];
  const serializedSlots = [];
  let preparationTime = 0;

  for (const slot of slots || []) {
    const items = (slot.itemIds || []).map((id) => byId.get(id)).filter(Boolean);
    for (const item of items) {
      preparationTime = Math.max(preparationTime, Number(item.preparationTime) || 0);
    }
    if (slot.kind === 'fixed') {
      for (const item of items) {
        fixed.push({ itemId: item.itemId, name: item.name, quantity: Number(slot.quantity) || 1 });
      }
      serializedSlots.push(serializeSlot(slot));
    } else {
      const groupId = groupIdsBySlot?.[slot.slotId] || slot.groupId || null;
      if (groupId) modifierGroups.push(groupId);
      serializedSlots.push(serializeSlot(slot, groupId, optionItemIdsBySlot?.[slot.slotId]));
    }
  }

  const price = Number(pricing.price) || 0;
  const sum = Number(pricing.componentsSum) || 0;
  const badge = (presentation.badge || '').trim();

  const payload = {
    categoryId: presentation.categoryId,
    name: (presentation.name || '').trim(),
    description: (presentation.description || '').trim(),
    price,
    preparationTime: preparationTime || 15,
    isCombo: true,
    comboItems: {
      fixed,
      groups: [],
      templateId: templateId || CUSTOM_TEMPLATE_ID,
      templateVersion: COMBO_TEMPLATE_VERSION,
      slots: serializedSlots,
    },
    modifierGroups: [...new Set(modifierGroups)],
    isPromotion: badge.length > 0,
  };
  if (presentation.imageUrl) payload.imageUrl = presentation.imageUrl;
  if (presentation.availability) payload.availability = presentation.availability;
  if (sum > price) payload.originalPrice = sum;
  if (badge) payload.promotionBadge = badge;
  return payload;
}

/**
 * Reconstruye el estado del wizard desde un item combo creado con plantilla.
 * Devuelve null si el item no tiene metadatos de plantilla.
 */
export function hydrateFromComboItem(item, categories) {
  const meta = item?.comboItems;
  if (!item?.isCombo || !meta?.templateId || !Array.isArray(meta.slots)) return null;
  const template = getComboTemplate(meta.templateId);
  const definitions = new Map((template?.slots || []).map((slot) => [slot.id, slot]));
  const byId = indexItems(categories);

  const slots = meta.slots.map((saved) => {
    const definition = definitions.get(saved.slotId);
    const firstItem = (saved.itemIds || []).map((id) => byId.get(id)).find(Boolean);
    const guessed = definition ? guessCategoryId(categories, definition.hints) : '';
    return {
      slotId: saved.slotId,
      kind: saved.kind,
      label: saved.label || '',
      labelKey:
        saved.labelKey ||
        definition?.labelKey ||
        `comboWizard.slots.custom${saved.kind === 'fixed' ? 'Fixed' : 'Choice'}`,
      quantity: saved.kind === 'fixed' ? Number(saved.quantity) || 1 : undefined,
      min: saved.kind === 'choice' ? Number(saved.min) || 0 : undefined,
      max: saved.kind === 'choice' ? Number(saved.max) || 1 : undefined,
      categoryId: firstItem?.categoryId || guessed || '',
      itemIds: (saved.itemIds || []).filter((id) => byId.has(id)),
      extraPrices: { ...(saved.extraPrices || {}) },
      groupId: saved.groupId || null,
      // RES-61: el mapa guardado vuelve al estado del wizard. Sin esto, reabrir y guardar un
      // combo lo perdía si el guardado no lo podía recalcular, y el motor caía al match por
      // nombre (`resolveSlotItemId`), que es justo lo que este ticket saca del medio.
      optionItemIds: Object.fromEntries(
        Object.entries(saved.optionItemIds || {}).filter(([, itemId]) => byId.has(itemId))
      ),
    };
  });

  return {
    templateId: meta.templateId,
    slots,
    presentation: {
      name: item.name || '',
      description: item.description || '',
      badge: item.promotionBadge || '',
      categoryId: item.categoryId || '',
      imageUrl: item.imageUrl || '',
      availability: item.availability || null,
    },
    price: Number(item.price) || 0,
  };
}

/** Marca que la categoría destino hay que crearla al guardar. */
export function isNewCombosCategory(categoryId) {
  return categoryId === NEW_COMBOS_CATEGORY;
}

/** Busca una categoría existente que parezca la de combos (por nombre). */
export function findCombosCategory(categories) {
  return (categories || []).find((cat) => normalizeText(cat.name).includes('combo'));
}
